import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberLocal.module.scss";

/* Art (same vibe as Welcome) */
import mrImg from "@/assets/UI/memberCreate/q3Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q3MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q3Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q3MrsM.webp";

/* Enums */
import { COUNTRIES } from "@/enums/MemberEnums";

const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

export default function CreateMemberLocal() {
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [wantSecondary, setWantSecondary] = useState("no"); // "yes" | "no"
  const [secondaryLanguage, setSecondaryLanguage] = useState("en"); // default English

  const t = (k, o) => i18n.t(k, o);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const title = t("onboarding.local.title", {
    defaultValue: "Now tell me where we can actually find you 😉",
  });
  const p1 = t("onboarding.local.p1", { defaultValue: "" });
  const p2 = t("onboarding.local.p2", { defaultValue: "" });
  const p3 = t("onboarding.local.p3", { defaultValue: "" });

  const countryLabel = t("onboarding.local.country.label", {
    defaultValue: "Country",
  });
  const countryHint = t("onboarding.local.country.hint", {
    defaultValue: "Pick the country you’re in.",
  });

  const cityLabel = t("onboarding.local.city.label", { defaultValue: "City" });
  const cityHint = t("onboarding.local.city.hint", {
    defaultValue: "Type your city name.",
  });

  const langIntro = t("onboarding.local.lang.intro", { defaultValue: "" });
  const langQ = t("onboarding.local.lang.q", { defaultValue: "" });

  const yesLabel = t("common.yesFancy", {
    defaultValue: "Yes, I’d like to add another language I’m comfortable with",
  });
  const noLabel = t("common.noFancy", {
    defaultValue: "No, keep me in English only for now",
  });

  const chooseLabel = t("onboarding.local.lang.choose", {
    defaultValue: "Choose your second language",
  });

  const doneMsg = t("onboarding.local.done", { defaultValue: "" });
  const closer = t("onboarding.local.closer", { defaultValue: "" });

  const cta = t("common.continue", { defaultValue: "Continue" });

  const languageOptions = [
    { code: "en", label: t("languages.en", { defaultValue: "English" }) },
    { code: "si", label: t("languages.si", { defaultValue: "Sinhala" }) },
    { code: "hi", label: t("languages.hi", { defaultValue: "Hindi" }) },
    { code: "ta", label: t("languages.ta", { defaultValue: "Tamil" }) },
  ];

  const yesNoOptions = [
    {
      value: "yes",
      emoji: "💖",
      title: yesLabel,
      hint: t("onboarding.local.lang.yesHint", {
        defaultValue: "Great. Pick the one you’re most comfortable with.",
      }),
    },
    {
      value: "no",
      emoji: "⚡",
      title: noLabel,
      hint: t("onboarding.local.lang.noHint", {
        defaultValue: "Perfect. We’ll keep things simple in English.",
      }),
    },
  ];

  async function onSubmit(e) {
    e.preventDefault();

    if (!country) {
      toast.info(
        t("onboarding.local.errors.country", {
          defaultValue: "Please select your country.",
        }),
      );
      return;
    }
    if (!city.trim()) {
      toast.info(
        t("onboarding.local.errors.city", {
          defaultValue: "Please enter your city.",
        }),
      );
      return;
    }

    const payload = {
      country,
      city: city.trim(),
      secondaryLanguage: wantSecondary === "yes" ? secondaryLanguage : "en",
    };

    setSubmitting(true);
    try {
      const { data } = await api.post("/v1/member-create/local", payload, {
        skipToast: true,
      });
      toast.success(t("onboarding.local.saved", { defaultValue: "Saved!" }));
      navigate(data?.next || "/member/avatar", { replace: true });
    } catch (err) {
      console.error("save local failed:", err);
      toast.error(
        t("onboarding.local.error", { defaultValue: "Something went wrong." }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Content layer */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>

                {(p1 || p2 || p3) && (
                  <div className={cls.formSub}>
                    {[p1, p2, p3].filter(Boolean).map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <form className={cls.form} onSubmit={onSubmit} noValidate>
              {/* Country */}
              <label className={cls.field}>
                <span className={cls.fieldTop}>
                  <span className={cls.fieldLabel}>{countryLabel}</span>
                  <small className={cls.fieldHint}>{countryHint}</small>
                </span>

                <div className={cls.selectWrap}>
                  <select
                    className={cls.select}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    aria-label={countryLabel}
                  >
                    <option value="">
                      {t("common.pickOne", { defaultValue: "Pick one…" })}
                    </option>
                    {COUNTRIES.filter(Boolean).map((c) => (
                      <option key={c} value={c}>
                        {c === "not_selected"
                          ? t("common.notSelected", {
                              defaultValue: "Not selected",
                            })
                          : c}
                      </option>
                    ))}
                  </select>

                  <span className={cls.selectChevron} aria-hidden="true">
                    ▾
                  </span>
                  <span className={cls.selectGlow} aria-hidden="true" />
                </div>
              </label>

              {/* City */}
              <label className={cls.field}>
                <span className={cls.fieldTop}>
                  <span className={cls.fieldLabel}>{cityLabel}</span>
                  <small className={cls.fieldHint}>{cityHint}</small>
                </span>

                <input
                  className={cls.input}
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder={t("onboarding.local.city.placeholder", {
                    defaultValue: "Type your city…",
                  })}
                  autoComplete="address-level2"
                />
              </label>

              {/* Secondary language */}
              <div className={cls.block}>
                {(langIntro || langQ) && (
                  <div className={cls.blockText}>
                    {langIntro && <p className={cls.blockP}>{langIntro}</p>}
                    {langQ && <p className={cls.blockP}>{langQ}</p>}
                  </div>
                )}

                <fieldset className={cls.options}>
                  <legend className={cls.srOnly}>
                    {t("onboarding.local.lang.legend", {
                      defaultValue: "Secondary language",
                    })}
                  </legend>

                  {yesNoOptions.map((opt) => {
                    const checked = wantSecondary === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`${cls.option} ${
                          checked ? cls.optionActive : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="wantSecondary"
                          value={opt.value}
                          checked={checked}
                          onChange={() => setWantSecondary(opt.value)}
                        />

                        <span className={cls.optionBody}>
                          <span className={cls.optionEmoji} aria-hidden="true">
                            {opt.emoji}
                          </span>

                          <span className={cls.optionText}>
                            <strong>{opt.title}</strong>
                            <small>{opt.hint}</small>
                          </span>

                          <span className={cls.optionDot} aria-hidden="true" />
                        </span>
                      </label>
                    );
                  })}
                </fieldset>

                {wantSecondary === "yes" ? (
                  <label className={cls.field}>
                    <span className={cls.fieldTop}>
                      <span className={cls.fieldLabel}>{chooseLabel}</span>
                    </span>

                    <div className={cls.selectWrap}>
                      <select
                        className={cls.select}
                        value={secondaryLanguage}
                        onChange={(e) => setSecondaryLanguage(e.target.value)}
                        aria-label={chooseLabel}
                      >
                        {languageOptions.map((opt) => (
                          <option key={opt.code} value={opt.code}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      <span className={cls.selectChevron} aria-hidden="true">
                        ▾
                      </span>
                      <span className={cls.selectGlow} aria-hidden="true" />
                    </div>
                  </label>
                ) : (
                  doneMsg && <div className={cls.note}>{doneMsg}</div>
                )}

                {closer && <div className={cls.closer}>{closer}</div>}
              </div>

              {/* Sticky submit like Welcome */}
              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={submitting || !country || !city.trim()}
                  aria-busy={submitting ? "true" : "false"}
                >
                  {submitting
                    ? t("common.saving", { defaultValue: "Saving..." })
                    : cta}
                </button>
              </div>
            </form>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
